import { ReactElement } from 'react'
import { Flex, Text } from 'uikit'
import styled, { DefaultTheme } from 'styled-components'

type Status = 'expired' | 'live' | 'next' | 'soon' | 'canceled' | 'calculating'

interface CardHeaderProps {
  status: Status
  title: string
  epoch: number
  icon?: ReactElement
}

const HEADER_HEIGHT = '37px'

// Used to get the gradient for the card border, which depends on the header color to create the illusion
// that header is overlapping the 1px card border.
// 'live' is not included into the switch case because it has isActive border style
export const getBorderBackground = (theme: DefaultTheme, status: Status) => {
  const gradientStopPoint = `calc(${HEADER_HEIGHT} + 1px)`
  switch (status) {
    case 'calculating':
      return `linear-gradient(transparent ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint}), ${theme.colors.gradients.cardHeader}`
    case 'canceled':
      return `linear-gradient(${theme.colors.warning} ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint})`
    case 'next':
      return `linear-gradient(${theme.colors.secondary} ${gradientStopPoint}, ${theme.colors.cardBorder} ${gradientStopPoint})`
    case 'expired':
    case 'soon':
    default:
      return theme.colors.cardBorder
  }
}

const getBackgroundColor = (theme: DefaultTheme, status: Status) => {
  switch (status) {
    case 'calculating':
    case 'live':
      return 'transparent'
    case 'next':
      return 'linear-gradient(90deg, #0AC4DD 0%, #47EDF8 101.33%);'
    case 'soon':
      return '#39D9FF0D'
    case 'expired':
    case 'canceled':
    default:
      return '#C9C9C9'
  }
}

type TextColor = 'textDisabled' | 'white' | 'secondary' | 'text' | 'textSubtle' | 'textRemark' | '#39D9FF'
type FallbackColor = 'text' | 'textSubtle' | 'textRemark'

const getTextColorByStatus = (status: Status, fallback: FallbackColor): TextColor => {
  switch (status) {
    case 'expired':
      return 'white'
    case 'next':
      return 'white'
    case 'live':
      return 'text'
    case 'canceled':
      return 'white'
    case 'calculating':
      return 'text'
    case 'soon':
      return '#39D9FF'
    default:
      return fallback
  }
}

const getSubTextColorByStatus = (status: Status, fallback: FallbackColor): TextColor => {
  switch (status) {
    case 'expired':
      return 'white'
    case 'next':
      return 'white'
    case 'calculating':
    case 'live':
      return 'textRemark'
    case 'canceled':
      return 'white'
    case 'soon':
      return '#39D9FF'
    default:
      return fallback
  }
}

const StyledCardHeader = styled.div<{ status: Status }>`
  align-items: center;
  background: ${({ theme, status }) => getBackgroundColor(theme, status)};
  display: flex;
  justify-content: space-between;
  height: ${HEADER_HEIGHT};
  padding: 16px;
`

const Round = styled.div`
  justify-self: center;
`

const CardHeader: React.FC<CardHeaderProps> = ({ status, title, epoch, icon }) => {
  const textColor = getTextColorByStatus(status, 'text')
  const isLive = status === 'live'

  return (
    <StyledCardHeader status={status}>
      <Flex alignItems="center">
        {icon}
        <Text
          color={textColor}
          bold
          textTransform={isLive ? 'uppercase' : 'capitalize'}
          lineHeight="21px"
          fontSize="14px"
          ml="8px"
        >
          {title}
        </Text>
      </Flex>
      <Round>
        <Text fontSize="14px" color={getSubTextColorByStatus(status, 'textRemark')} textAlign="center">
          {`#${epoch}`}
        </Text>
      </Round>
    </StyledCardHeader>
  )
}

export default CardHeader
